import React, { useCallback, useMemo, useReducer, useState } from 'react';
import { Alert, Button } from 'reactstrap';
import { useQuery } from '@tanstack/react-query';
import { RiFilter3Fill, RiAddLine } from 'react-icons/ri';
import { SortingState } from '@tanstack/react-table';
import { toast, Bounce } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import DataTable from '@/common/tableData/DataTable';

import Paginator from '@/pages/common/paginator';
import { convertSortingToApiFormat } from '@/common/utils/convert-sorting-to-api-format';
import {
  fetchCountriesRest,
  createCountryRest,
  updateCountryRest,
  deleteCountryRest,
} from '@/services/countries/countries-service';

import { initialState, queryReducer } from '@/pages/agents/Countries/countries-query-reducer';
import { createColumns } from '@/pages/agents/Countries/countries.columns';
import { AdminCountriesResponseDto, AdminCountryInfoDto, CreateCountryDto, UpdateCountryDto } from '@repo/common-types';
import CountriesFilter from './components/CountriesFilter';
import CountryFormModal from './components/CountryFormModal';
import StandardLayout from '@/common/layouts/StandardLayout';
import DebouncedInput from '@/components/DebounceInput';
import ConfirmModal from '@/components/ConfirmationModal/ConfirmModal';

const CountriesPage: React.FC = () => {
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [queryParams, dispatch] = useReducer(queryReducer, initialState);

  // Modal state management
  const [showCountryModal, setShowCountryModal] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<AdminCountryInfoDto | undefined>(undefined);

  // Confirmation modal state management
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedCountryId, setSelectedCountryId] = useState<string | undefined>(undefined);

  const { sorting, ...rest } = queryParams;

  const { data, isLoading, error, refetch } = useQuery<AdminCountriesResponseDto>({
    queryKey: ['countriesData', queryParams],
    queryFn: () =>
      fetchCountriesRest({
        skip: rest.skip,
        take: rest.take,
        filter: rest.filter,
        sorts: convertSortingToApiFormat(sorting),
      }),
    staleTime: 60000,
  });

  const handleSortingChange = useCallback(
    (updater: SortingState | ((old: SortingState) => SortingState)) => {
      const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
      dispatch({ type: 'SET_SORTING', payload: newSorting });
    },
    [sorting],
  );

  const handleCreateCountry = useCallback(() => {
    setSelectedCountry(undefined);
    setShowCountryModal(true);
  }, []);

  const handleEditCountry = useCallback(
    (countryId: string) => {
      const country = data?.countries?.find((c) => c.id === countryId);
      if (country) {
        setSelectedCountry(country);
        setShowCountryModal(true);
      }
    },
    [data?.countries],
  );

  const handleDeleteCountry = (countryId: string) => {
    setSelectedCountryId(countryId);
    setShowConfirmModal(true);
  };

  const handleConfirmDeleteCountry = useCallback(async () => {
    try {
      if (!selectedCountryId) throw new Error('No country selected');
      const country = data?.countries?.find((c) => c.id === selectedCountryId);
      if (!country) return;
      if (country.regionsCount > 0) {
        throw new Error('Cannot delete country with regions assigned.');
      }
      await deleteCountryRest(selectedCountryId);
      await refetch();

      toast.success(`Country "${country.name}" deleted successfully.`, {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'dark',
        transition: Bounce,
      });
    } catch (error) {
      console.error('Error deleting country:', error);

      const specificError = error instanceof Error ? error.message : '';
      const title = 'Failed to delete country.';
      const errorMessage = specificError ? `${title}\n\n${specificError}` : `${title} Please try again.`;

      toast.error(errorMessage, {
        position: 'top-right',
        autoClose: 8000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'dark',
        transition: Bounce,
      });
    } finally {
      setShowConfirmModal(false);
      setSelectedCountryId(undefined);
    }
  }, [data?.countries, refetch]);

  const handleViewSubdivisions = useCallback(
    (countryId: string) => {
      const country = data?.countries?.find((c) => c.id === countryId);
      if (country) {
        setSelectedCountry(country);
        setShowCountryModal(true);
      }
    },
    [data?.countries],
  );

  const handleAddSubdivisions = useCallback(
    (countryId: string) => {
      const country = data?.countries?.find((c) => c.id === countryId);
      if (country) {
        setSelectedCountry(country);
        setShowCountryModal(true);
      }
    },
    [data?.countries],
  );

  const handleModalClose = useCallback(() => {
    setShowCountryModal(false);
    setSelectedCountry(undefined);
  }, []);

  const handleFormSubmit = useCallback(
    async (countryData: CreateCountryDto | UpdateCountryDto) => {
      try {
        if (selectedCountry) {
          await updateCountryRest(selectedCountry.id, countryData as UpdateCountryDto);
          toast.success('Country updated successfully.', {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: 'dark',
            transition: Bounce,
          });
        } else {
          await createCountryRest(countryData as CreateCountryDto);
          toast.success('Country created successfully.', {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: 'dark',
            transition: Bounce,
          });
        }

        await refetch();
        handleModalClose();
      } catch (error) {
        console.error('Error submitting country:', error);

        const specificError = error instanceof Error ? error.message : '';
        const action = selectedCountry ? 'update' : 'create';
        const title = `Failed to ${action} country.`;
        const errorMessage = specificError ? `${title}\n\n${specificError}` : `${title} Please try again.`;

        toast.error(errorMessage, {
          position: 'top-right',
          autoClose: 8000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'dark',
          transition: Bounce,
        });
      }
    },
    [selectedCountry, refetch, handleModalClose],
  );

  const handlePaginationChange = useCallback((skip: number, take: number) => {
    dispatch({ type: 'SET_PAGINATION', payload: { skip, take } });
  }, []);

  // Check if filters differ from initial state (empty object)
  const hasActiveFilters = useMemo(() => {
    const currentFilter = queryParams.filter || {};
    return (
      Object.keys(currentFilter).length > 0 &&
      Object.values(currentFilter).some((value) => value !== undefined && value !== '')
    );
  }, [queryParams.filter]);

  const handleSearch = useCallback((value: string | number) => {
    dispatch({ type: 'SET_SEARCH', payload: value.toString() });
  }, []);

  const columns = useMemo(
    () => createColumns(handleEditCountry, handleDeleteCountry),
    [handleEditCountry, handleDeleteCountry],
  );

  return (
    <StandardLayout
      pageTitle="Countries & Subdivisions"
      headerButton={
        <Button color="primary" className="d-flex align-items-center gap-2" onClick={handleCreateCountry}>
          <RiAddLine />
          Add New Country
        </Button>
      }
    >
      {error ? (
        <Alert color="danger">Error when fetching countries data</Alert>
      ) : (
        <>
          <div className="w-100 d-flex justify-content-between align-items-center mb-4 gap-2">
            <div className="d-flex align-items-center justify-content-start gap-2">
              <DebouncedInput
                className="form-control w-auto"
                placeholder="Search"
                isLoading={false}
                onChange={handleSearch}
              />
              <Button onClick={() => setShowFilterModal(true)} type="button" outline color="dark">
                <span className="d-flex align-items-center gap-2">
                  <RiFilter3Fill size={20} />
                  Filter
                </span>
              </Button>
              {hasActiveFilters && (
                <Button type="button" outline color="dark" onClick={() => dispatch({ type: 'RESET_FILTERS' })}>
                  Clear Filters
                </Button>
              )}
            </div>
          </div>
          {/* <CountriesTable /> */}
          <DataTable
            data={data?.countries || []}
            columns={columns}
            sorting={sorting}
            onSortingChange={handleSortingChange}
            enableSorting={true}
          />

          <Paginator
            take={queryParams.take || 25}
            skip={queryParams.skip || 0}
            showing={data?.countries?.length || 0}
            totalCount={data?.totalCount || 0}
            setTake={(take) => handlePaginationChange(queryParams.skip || 0, take)}
            setSkip={(skip) => handlePaginationChange(skip, queryParams.take || 25)}
          />

          {/* Filter Modal */}
          {showFilterModal && (
            <CountriesFilter
              onClose={() => setShowFilterModal(false)}
              onApplyFilters={(filters) => {
                dispatch({ type: 'SET_FILTERS', payload: filters });
                setShowFilterModal(false);
              }}
              currentFilters={queryParams.filter}
            />
          )}

          {/* Country Form Modal */}
          {showCountryModal && (
            <CountryFormModal
              country={selectedCountry}
              onClose={handleModalClose}
              onSubmit={handleFormSubmit}
              isLoading={false} // TODO: Add loading state management
            />
          )}
          {showConfirmModal && (
            <ConfirmModal
              onClose={() => {
                setShowConfirmModal(false);
                setSelectedCountryId(undefined);
              }}
              onConfirm={handleConfirmDeleteCountry}
              title="Confirm"
              message="Are you sure you want to delete this country?"
            />
          )}
        </>
      )}
    </StandardLayout>
  );
};

export default CountriesPage;
